import { t } from "i18next";
import { useState } from "react";
import { breedingLinks } from "../../../api/adminDirectory";
import { deleteBreeding } from "../../../api/breeding";
import { enumsectionToI18nKey } from "../../../generated/enums";
import { Account, Breeding, Building } from "../../../generated/types";
import { useAdminDirectory } from "../../../hooks/useAdminDirectory";
import { isAdminAccount } from "../../../utils/roles";
import { TableDetails, TicketNote } from "../../common/TableDetails";
import { TitleSection } from "../../common/TitleSection";
import { sectionToIcon } from "../../type/default";
import { BuildingSection } from "../building/BuildingSection";
import { BuildingTicket } from "../building/BuildingTicket";
import { CreateBuildingModal } from "../building/CreateBuildingModal";
import { AccountGroups } from "../user/AccountGroups";
import { CompanySection } from "../user/CompanySection";
import { UserTicket } from "../user/UserTicket";
import { BreedingSection } from "./BreedingSection";
import { DeleteEmptyModal } from "./DeleteEmptyModal";

type BreedingTicketProps = {
  breeding: Breeding;
  onClose: () => void;
  /** Rafraîchit la page sous les tickets. */
  onDataRefresh?: () => void;
  useBackIcon?: boolean;
};

export const BreedingTicket = ({
  breeding: initialBreeding,
  onClose,
  onDataRefresh,
  useBackIcon = true,
}: BreedingTicketProps) => {
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(
    null
  );
  const [selectedUser, setSelectedUser] = useState<Account | null>(null);
  const [isCreateBuildingOpen, setIsCreateBuildingOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { directory, isLoading, failed, reload } = useAdminDirectory();
  const { breeding, company, buildings, accounts, installers } = breedingLinks(
    directory,
    initialBreeding
  );
  const refresh = () => {
    reload();
    onDataRefresh?.();
  };

  return (
    <div className="space-y-4">
      <TableDetails
        title={t(enumsectionToI18nKey["section_breeding"])}
        icon={sectionToIcon.section_breeding}
        onClose={onClose}
        useBackIcon={useBackIcon}
        onDelete={isAdminAccount() ? () => setIsDeleteOpen(true) : undefined}
        deleteText={t("breeding-delete")}
        deleteDisabledText={
          buildings.length > 0 ? t("breeding-delete-not-empty") : ""
        }
      >
        <div className="p-8 space-y-4">
          <BreedingSection
            breeding={breeding}
            detailLevel="maxi"
            onUpdate={refresh}
          />

          {isLoading || failed ? (
            <TicketNote>
              {failed
                ? String(t("ticket-links-error"))
                : `${String(t("loading"))}…`}
            </TicketNote>
          ) : (
            <>
              <TitleSection
                title={String(t(enumsectionToI18nKey["section_building"]))}
                icon={sectionToIcon.section_building}
                onClick={() => setIsCreateBuildingOpen(true)}
              />
              {buildings.length > 0 ? (
                <div className="border-l-2 border-secondary pl-4 ml-2">
                  {buildings.map((building) => (
                    <BuildingSection
                      key={building.id}
                      building={building}
                      detailLevel={buildings.length > 3 ? "mini" : "default"}
                      onClick={() => setSelectedBuilding(building)}
                    />
                  ))}
                </div>
              ) : (
                <TicketNote>{String(t("ticket-no-building"))}</TicketNote>
              )}

              <TitleSection
                title={String(t(enumsectionToI18nKey["section_accounts"]))}
                icon={sectionToIcon.section_accounts}
              />
              {accounts.length + installers.length > 0 ? (
                <AccountGroups
                  accounts={[...accounts, ...installers]}
                  directory={directory}
                  onSelect={setSelectedUser}
                />
              ) : (
                <TicketNote>{String(t("ticket-no-account"))}</TicketNote>
              )}
              {company && <CompanySection company={company} />}
            </>
          )}
        </div>
      </TableDetails>

      {selectedBuilding && (
        <BuildingTicket
          building={selectedBuilding}
          onClose={() => setSelectedBuilding(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      {selectedUser && (
        <UserTicket
          account={selectedUser}
          onClose={() => setSelectedUser(null)}
          onDataRefresh={onDataRefresh}
        />
      )}

      <CreateBuildingModal
        breedingId={breeding.id}
        breedingName={breeding.name}
        companyId={breeding.id_company}
        companyName={company?.company_name ?? ""}
        isOpen={isCreateBuildingOpen}
        onClose={() => setIsCreateBuildingOpen(false)}
        onSuccess={refresh}
      />

      <DeleteEmptyModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title={t("breeding-delete")}
        warningText={t("breeding-delete-warning", { name: breeding.name })}
        blockedText=""
        onDelete={() => deleteBreeding(breeding.id)}
        onDeleted={() => {
          setIsDeleteOpen(false);
          onClose();
          refresh();
        }}
      />
    </div>
  );
};
